import { useMutation, useQuery } from '@apollo/client';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CashRegisterFormDialog, type CashRegisterFormValues } from './CashRegisterFormDialog';
import {
  ConsultingRoomFormDialog,
  type ConsultingRoomFormValues,
  type ConsultingRoomZoneType,
} from './ConsultingRoomFormDialog';
import {
  ACTIVE_WORKSTATIONS_QUERY,
  CASH_REGISTERS_QUERY,
  CONSULTING_ROOMS_QUERY,
  CREATE_CASH_REGISTER_MUTATION,
  CREATE_CONSULTING_ROOM_MUTATION,
  CREATE_WORKSTATION_MUTATION,
  PET_SPECIES_CATALOG_QUERY,
  SET_WAITING_ROOM_DISPLAY_SETTINGS_MUTATION,
  SETTINGS_BRANCHES_QUERY,
  SET_PET_SPECIES_CATALOG_MUTATION,
  SET_CASH_REGISTER_STATUS_MUTATION,
  SET_CONSULTING_ROOM_STATUS_MUTATION,
  SET_WORKSTATION_STATUS_MUTATION,
  UPDATE_CASH_REGISTER_MUTATION,
  UPDATE_CONSULTING_ROOM_MUTATION,
  UPDATE_WORKSTATION_MUTATION,
  WAITING_ROOM_DISPLAY_SETTINGS_QUERY,
  WORKSTATIONS_QUERY,
} from './settings.gql';
import { WorkstationFormDialog, type WorkstationFormValues } from './WorkstationFormDialog';

import { hasPermission } from '@/modules/auth/useAuth';
import { assertMutationSucceeded, errorMessage } from '@/shared/api/graphql-errors';
import { CrudTable, type CrudColumn } from '@/shared/crud/CrudTable';
import { CrudToolbar } from '@/shared/crud/CrudToolbar';
import { useCrudList } from '@/shared/crud/useCrudList';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { showToast } from '@/shared/ui/toast';
import { useAuthStore } from '@/stores/authStore';

type SettingsTab =
  | 'workstations'
  | 'consulting_rooms'
  | 'cash_registers'
  | 'species'
  | 'display_boards';
type WorkstationKind = 'cashier' | 'consulting_room' | 'admin' | 'mixed';

interface Branch {
  id: string;
  name: string;
}

interface SettingsBranchesResponse {
  branches: Branch[];
}

interface PageInfo {
  total: number;
  offset: number;
  limit: number;
  has_more: boolean;
}

interface Workstation {
  id: string;
  branch_id: string;
  kind: string;
  display_name: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

interface WorkstationsResponse {
  workstations: {
    items: Workstation[];
    page: PageInfo;
  };
}

interface ConsultingRoom {
  id: string;
  branch_id: string;
  name: string;
  display_order: number;
  zone_type: ConsultingRoomZoneType;
  workstation_id: string | null;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

interface ConsultingRoomsResponse {
  consultingRooms: {
    items: ConsultingRoom[];
    page: PageInfo;
  };
}

interface CashRegister {
  id: string;
  branch_id: string;
  name: string;
  workstation_id: string | null;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

interface CashRegistersResponse {
  cashRegisters: {
    items: CashRegister[];
    page: PageInfo;
  };
}

interface SpeciesOption {
  value: string;
  is_system: boolean;
}

interface WaitingRoomDisplaySettingsResponse {
  waitingRoomDisplaySettings: {
    youtube_playlist_id: string | null;
    is_configured: boolean;
  };
}

const WORKSTATION_KIND_TO_GQL: Record<
  WorkstationKind,
  'CASHIER' | 'CONSULTING_ROOM' | 'ADMIN' | 'MIXED'
> = {
  cashier: 'CASHIER',
  consulting_room: 'CONSULTING_ROOM',
  admin: 'ADMIN',
  mixed: 'MIXED',
};

const CONSULTING_ROOM_ZONE_TYPE_TO_GQL: Record<
  ConsultingRoomZoneType,
  'CONSULTING_ROOM' | 'XRAY' | 'ULTRASOUND' | 'OTHER'
> = {
  consulting_room: 'CONSULTING_ROOM',
  xray: 'XRAY',
  ultrasound: 'ULTRASOUND',
  other: 'OTHER',
};

function mapWorkstationKind(value: string): WorkstationKind {
  switch (value.toLowerCase()) {
    case 'cashier':
      return 'cashier';
    case 'consulting_room':
      return 'consulting_room';
    case 'admin':
      return 'admin';
    case 'mixed':
      return 'mixed';
    default:
      return 'consulting_room';
  }
}

function mapConsultingRoomZoneType(value: string): ConsultingRoomZoneType {
  switch (value.toLowerCase()) {
    case 'consulting_room':
      return 'consulting_room';
    case 'xray':
      return 'xray';
    case 'ultrasound':
      return 'ultrasound';
    case 'other':
      return 'other';
    default:
      return 'consulting_room';
  }
}

function toNullableId(value: string | undefined): string | null {
  return value && value.length > 0 ? value : null;
}

function speciesLabel(value: string): string {
  if (value === 'canino') return 'Canino';
  if (value === 'felino') return 'Felino';
  if (value.length === 0) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function SettingsOpsPage(): JSX.Element {
  const { t } = useTranslation();
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);
  const can_manage_all = hasPermission(permissions, 'admin.users.manage');
  const visible_tabs = useMemo<readonly SettingsTab[]>(
    () =>
      can_manage_all
        ? ['workstations', 'consulting_rooms', 'cash_registers', 'species', 'display_boards']
        : ['display_boards'],
    [can_manage_all],
  );
  const [tab, setTab] = useState<SettingsTab>(can_manage_all ? 'workstations' : 'display_boards');
  const [branch_id, setBranchId] = useState<string>('');

  const ws_list = useCrudList();
  const rooms_list = useCrudList();
  const registers_list = useCrudList();

  const [creating_ws, setCreatingWs] = useState(false);
  const [editing_ws, setEditingWs] = useState<Workstation | null>(null);
  const [creating_room, setCreatingRoom] = useState(false);
  const [editing_room, setEditingRoom] = useState<ConsultingRoom | null>(null);
  const [creating_register, setCreatingRegister] = useState(false);
  const [editing_register, setEditingRegister] = useState<CashRegister | null>(null);
  const [new_species, setNewSpecies] = useState('');
  const [waiting_room_playlist, setWaitingRoomPlaylist] = useState('');

  const { data: branches_data } = useQuery<SettingsBranchesResponse>(SETTINGS_BRANCHES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const branches = useMemo(() => branches_data?.branches ?? [], [branches_data]);

  useEffect(() => {
    if (branch_id || branches.length === 0) return;
    setBranchId(branches[0]?.id ?? '');
  }, [branch_id, branches]);

  const { data: active_workstations_data, refetch: refetch_active_workstations } =
    useQuery<WorkstationsResponse>(ACTIVE_WORKSTATIONS_QUERY, {
      variables: { branch_id: branch_id || null },
      skip: !branch_id,
      fetchPolicy: 'cache-and-network',
    });
  const active_workstations = useMemo(
    () =>
      (active_workstations_data?.workstations.items ?? []).map((row) => ({
        ...row,
        kind: mapWorkstationKind(row.kind),
      })),
    [active_workstations_data],
  );

  const workstation_name_by_id = useMemo(() => {
    const map = new Map<string, string>();
    for (const ws of active_workstations) map.set(ws.id, ws.display_name);
    return map;
  }, [active_workstations]);

  const room_workstation_options = active_workstations
    .filter((ws) => ws.kind === 'consulting_room' || ws.kind === 'mixed')
    .map((ws) => ({ id: ws.id, display_name: ws.display_name }));
  const register_workstation_options = active_workstations
    .filter((ws) => ws.kind === 'cashier' || ws.kind === 'mixed')
    .map((ws) => ({ id: ws.id, display_name: ws.display_name }));

  const ws_query = useQuery<WorkstationsResponse>(WORKSTATIONS_QUERY, {
    variables: {
      offset: ws_list.offset,
      limit: ws_list.limit,
      search: ws_list.search || null,
      status_filter: ws_list.status_filter,
      branch_id: branch_id || null,
    },
    skip: tab !== 'workstations' || !branch_id,
    fetchPolicy: 'cache-and-network',
  });

  const rooms_query = useQuery<ConsultingRoomsResponse>(CONSULTING_ROOMS_QUERY, {
    variables: {
      offset: rooms_list.offset,
      limit: rooms_list.limit,
      search: rooms_list.search || null,
      status_filter: rooms_list.status_filter,
      branch_id: branch_id || null,
    },
    skip: tab !== 'consulting_rooms' || !branch_id,
    fetchPolicy: 'cache-and-network',
  });

  const registers_query = useQuery<CashRegistersResponse>(CASH_REGISTERS_QUERY, {
    variables: {
      offset: registers_list.offset,
      limit: registers_list.limit,
      search: registers_list.search || null,
      status_filter: registers_list.status_filter,
      branch_id: branch_id || null,
    },
    skip: tab !== 'cash_registers' || !branch_id,
    fetchPolicy: 'cache-and-network',
  });

  const [create_workstation, create_workstation_state] = useMutation(CREATE_WORKSTATION_MUTATION);
  const [update_workstation, update_workstation_state] = useMutation(UPDATE_WORKSTATION_MUTATION);
  const [set_workstation_status] = useMutation(SET_WORKSTATION_STATUS_MUTATION);

  const [create_room, create_room_state] = useMutation(CREATE_CONSULTING_ROOM_MUTATION);
  const [update_room, update_room_state] = useMutation(UPDATE_CONSULTING_ROOM_MUTATION);
  const [set_room_status] = useMutation(SET_CONSULTING_ROOM_STATUS_MUTATION);

  const [create_register, create_register_state] = useMutation(CREATE_CASH_REGISTER_MUTATION);
  const [update_register, update_register_state] = useMutation(UPDATE_CASH_REGISTER_MUTATION);
  const [set_register_status] = useMutation(SET_CASH_REGISTER_STATUS_MUTATION);

  const species_query = useQuery<{ petSpeciesCatalog: SpeciesOption[] }>(
    PET_SPECIES_CATALOG_QUERY,
    {
      skip: tab !== 'species',
      fetchPolicy: 'cache-and-network',
    },
  );
  const [set_species_catalog, set_species_catalog_state] = useMutation(
    SET_PET_SPECIES_CATALOG_MUTATION,
  );

  const waiting_room_display_settings_query = useQuery<WaitingRoomDisplaySettingsResponse>(
    WAITING_ROOM_DISPLAY_SETTINGS_QUERY,
    {
      skip: tab !== 'display_boards',
      fetchPolicy: 'cache-and-network',
    },
  );
  const [set_waiting_room_display_settings, set_waiting_room_display_settings_state] = useMutation(
    SET_WAITING_ROOM_DISPLAY_SETTINGS_MUTATION,
  );

  useEffect(() => {
    if (tab !== 'display_boards') return;
    const playlist_id =
      waiting_room_display_settings_query.data?.waitingRoomDisplaySettings.youtube_playlist_id;
    if (playlist_id === undefined) return;
    setWaitingRoomPlaylist(playlist_id ?? '');
  }, [tab, waiting_room_display_settings_query.data]);

  const no_branch_selected = tab !== 'species' && tab !== 'display_boards' && !branch_id;

  const showCreateSuccess = (): void => {
    showToast({
      tone: 'success',
      title: t('alerts.create_success_title'),
      body: t('alerts.create_success_body'),
    });
  };
  const showUpdateSuccess = (): void => {
    showToast({
      tone: 'success',
      title: t('alerts.update_success_title'),
      body: t('alerts.update_success_body'),
    });
  };

  const handleMutationError = (err: unknown): void => {
    const msg = errorMessage(err);
    if (msg.includes('cash_register_has_open_shift')) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: t('settings_ops.errors.cash_register_has_open_shift'),
      });
      return;
    }
    showToast({
      tone: 'error',
      title: t('alerts.update_error_title'),
      body: msg,
    });
  };

  const onCreateWorkstation = async (values: WorkstationFormValues): Promise<void> => {
    if (!branch_id) return;
    try {
      const result = await create_workstation({
        variables: {
          input: {
            branch_id,
            kind: WORKSTATION_KIND_TO_GQL[values.kind],
            display_name: values.display_name,
          },
        },
      });
      assertMutationSucceeded(result, 'create_workstation_failed');
      setCreatingWs(false);
      showCreateSuccess();
      await Promise.all([ws_query.refetch(), refetch_active_workstations()]);
    } catch (err) {
      handleMutationError(err);
    }
  };

  const onUpdateWorkstation = async (values: WorkstationFormValues): Promise<void> => {
    if (!editing_ws) return;
    try {
      const result = await update_workstation({
        variables: {
          id: editing_ws.id,
          input: {
            kind: WORKSTATION_KIND_TO_GQL[values.kind],
            display_name: values.display_name,
          },
        },
      });
      assertMutationSucceeded(result, 'update_workstation_failed');
      setEditingWs(null);
      showUpdateSuccess();
      await Promise.all([ws_query.refetch(), refetch_active_workstations()]);
    } catch (err) {
      handleMutationError(err);
    }
  };

  const onToggleWorkstationStatus = async (row: Workstation): Promise<void> => {
    if (!window.confirm(t('settings_ops.confirm_status_change'))) return;
    try {
      const result = await set_workstation_status({
        variables: {
          id: row.id,
          status: row.status === 'active' ? 'inactive' : 'active',
        },
      });
      assertMutationSucceeded(result, 'update_workstation_status_failed');
      showUpdateSuccess();
      await Promise.all([ws_query.refetch(), refetch_active_workstations()]);
    } catch (err) {
      handleMutationError(err);
    }
  };

  const onCreateRoom = async (values: ConsultingRoomFormValues): Promise<void> => {
    if (!branch_id) return;
    try {
      const result = await create_room({
        variables: {
          input: {
            branch_id,
            name: values.name,
            display_order: values.display_order,
            zone_type: CONSULTING_ROOM_ZONE_TYPE_TO_GQL[values.zone_type],
            workstation_id: toNullableId(values.workstation_id),
          },
        },
      });
      assertMutationSucceeded(result, 'create_consulting_room_failed');
      setCreatingRoom(false);
      showCreateSuccess();
      await rooms_query.refetch();
    } catch (err) {
      handleMutationError(err);
    }
  };

  const onUpdateRoom = async (values: ConsultingRoomFormValues): Promise<void> => {
    if (!editing_room) return;
    try {
      const result = await update_room({
        variables: {
          id: editing_room.id,
          input: {
            name: values.name,
            display_order: values.display_order,
            zone_type: CONSULTING_ROOM_ZONE_TYPE_TO_GQL[values.zone_type],
            workstation_id: toNullableId(values.workstation_id),
          },
        },
      });
      assertMutationSucceeded(result, 'update_consulting_room_failed');
      setEditingRoom(null);
      showUpdateSuccess();
      await rooms_query.refetch();
    } catch (err) {
      handleMutationError(err);
    }
  };

  const onToggleRoomStatus = async (row: ConsultingRoom): Promise<void> => {
    if (!window.confirm(t('settings_ops.confirm_status_change'))) return;
    try {
      const result = await set_room_status({
        variables: {
          id: row.id,
          status: row.status === 'active' ? 'inactive' : 'active',
        },
      });
      assertMutationSucceeded(result, 'update_consulting_room_status_failed');
      showUpdateSuccess();
      await rooms_query.refetch();
    } catch (err) {
      handleMutationError(err);
    }
  };

  const onCreateRegister = async (values: CashRegisterFormValues): Promise<void> => {
    if (!branch_id) return;
    try {
      const result = await create_register({
        variables: {
          input: {
            branch_id,
            name: values.name,
            workstation_id: toNullableId(values.workstation_id),
          },
        },
      });
      assertMutationSucceeded(result, 'create_cash_register_failed');
      setCreatingRegister(false);
      showCreateSuccess();
      await registers_query.refetch();
    } catch (err) {
      handleMutationError(err);
    }
  };

  const onUpdateRegister = async (values: CashRegisterFormValues): Promise<void> => {
    if (!editing_register) return;
    try {
      const result = await update_register({
        variables: {
          id: editing_register.id,
          input: {
            name: values.name,
            workstation_id: toNullableId(values.workstation_id),
          },
        },
      });
      assertMutationSucceeded(result, 'update_cash_register_failed');
      setEditingRegister(null);
      showUpdateSuccess();
      await registers_query.refetch();
    } catch (err) {
      handleMutationError(err);
    }
  };

  const onToggleRegisterStatus = async (row: CashRegister): Promise<void> => {
    if (!window.confirm(t('settings_ops.confirm_status_change'))) return;
    try {
      const result = await set_register_status({
        variables: {
          id: row.id,
          status: row.status === 'active' ? 'inactive' : 'active',
        },
      });
      assertMutationSucceeded(result, 'update_cash_register_status_failed');
      showUpdateSuccess();
      await registers_query.refetch();
    } catch (err) {
      handleMutationError(err);
    }
  };

  const onSaveSpeciesCatalog = async (values: string[]): Promise<void> => {
    try {
      const result = await set_species_catalog({ variables: { input: { values } } });
      assertMutationSucceeded(result, 'set_pet_species_catalog_failed');
      showUpdateSuccess();
      setNewSpecies('');
      await species_query.refetch();
    } catch (err) {
      handleMutationError(err);
    }
  };

  const onAddSpecies = async (): Promise<void> => {
    const candidate = new_species.trim();
    if (!candidate) return;
    const current = species_query.data?.petSpeciesCatalog ?? [];
    const exists = current.some((item) => item.value.toLowerCase() === candidate.toLowerCase());
    if (exists) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: t('settings_ops.species.duplicate_error'),
      });
      return;
    }
    await onSaveSpeciesCatalog([...current.map((item) => item.value), candidate]);
  };

  const onRemoveSpecies = async (value: string): Promise<void> => {
    const current = species_query.data?.petSpeciesCatalog ?? [];
    const target = current.find((item) => item.value === value);
    if (!target || target.is_system) return;
    if (!window.confirm(t('settings_ops.species.confirm_remove', { value }))) return;
    await onSaveSpeciesCatalog(
      current.filter((item) => item.value !== value).map((item) => item.value),
    );
  };

  const onSaveWaitingRoomPlaylist = async (): Promise<void> => {
    try {
      const result = await set_waiting_room_display_settings({
        variables: {
          input: {
            youtube_playlist: waiting_room_playlist.trim(),
          },
        },
      });
      const playlist_id = result.data?.setWaitingRoomDisplaySettings?.youtube_playlist_id ?? '';
      setWaitingRoomPlaylist(playlist_id);
      showUpdateSuccess();
      await waiting_room_display_settings_query.refetch();
    } catch (err) {
      handleMutationError(err);
    }
  };

  const workstation_rows =
    ws_query.data?.workstations.items.map((row) => ({
      ...row,
      kind: mapWorkstationKind(row.kind),
    })) ?? [];

  const room_rows =
    rooms_query.data?.consultingRooms.items.map((row) => ({
      ...row,
      zone_type: mapConsultingRoomZoneType(row.zone_type),
    })) ?? [];

  const species_rows = (species_query.data?.petSpeciesCatalog ?? []).slice().sort((a, b) => {
    if (a.is_system !== b.is_system) return a.is_system ? -1 : 1;
    return a.value.localeCompare(b.value, 'es', { sensitivity: 'base' });
  });

  const workstation_columns: CrudColumn<Workstation & { kind: WorkstationKind }>[] = [
    {
      key: 'display_name',
      header_key: 'settings_ops.workstations.display_name',
      render: (r) => r.display_name,
    },
    {
      key: 'kind',
      header_key: 'settings_ops.workstations.kind',
      width: 'w-44',
      render: (r) => <Badge variant="outline">{t(`workstation.kind_${r.kind}`)}</Badge>,
    },
    {
      key: 'status',
      header_key: 'common.status',
      width: 'w-28',
      align: 'center',
      render: (r) => (
        <Badge variant={r.status === 'active' ? 'success' : 'muted'}>
          {t(`status.${r.status}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header_key: 'common.actions',
      align: 'right',
      width: 'w-48',
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={() => setEditingWs(r)}>
            {t('common.edit')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              void onToggleWorkstationStatus(r);
            }}
          >
            {r.status === 'active' ? t('common.deactivate') : t('common.activate')}
          </Button>
        </div>
      ),
    },
  ];

  const room_columns: CrudColumn<ConsultingRoom>[] = [
    {
      key: 'name',
      header_key: 'settings_ops.consulting_rooms.name',
      render: (r) => r.name,
    },
    {
      key: 'display_order',
      header_key: 'settings_ops.consulting_rooms.display_order',
      width: 'w-24',
      align: 'center',
      render: (r) => r.display_order,
    },
    {
      key: 'zone_type',
      header_key: 'settings_ops.consulting_rooms.zone_type',
      render: (r) => t(`settings_ops.consulting_rooms.zone_type_${r.zone_type}`),
    },
    {
      key: 'workstation',
      header_key: 'settings_ops.workstation',
      render: (r) =>
        workstation_name_by_id.get(r.workstation_id ?? '') ?? t('settings_ops.workstation_none'),
    },
    {
      key: 'status',
      header_key: 'common.status',
      width: 'w-28',
      align: 'center',
      render: (r) => (
        <Badge variant={r.status === 'active' ? 'success' : 'muted'}>
          {t(`status.${r.status}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header_key: 'common.actions',
      align: 'right',
      width: 'w-48',
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={() => setEditingRoom(r)}>
            {t('common.edit')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              void onToggleRoomStatus(r);
            }}
          >
            {r.status === 'active' ? t('common.deactivate') : t('common.activate')}
          </Button>
        </div>
      ),
    },
  ];

  const register_columns: CrudColumn<CashRegister>[] = [
    {
      key: 'name',
      header_key: 'settings_ops.cash_registers.name',
      render: (r) => r.name,
    },
    {
      key: 'workstation',
      header_key: 'settings_ops.workstation',
      render: (r) =>
        workstation_name_by_id.get(r.workstation_id ?? '') ?? t('settings_ops.workstation_none'),
    },
    {
      key: 'status',
      header_key: 'common.status',
      width: 'w-28',
      align: 'center',
      render: (r) => (
        <Badge variant={r.status === 'active' ? 'success' : 'muted'}>
          {t(`status.${r.status}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header_key: 'common.actions',
      align: 'right',
      width: 'w-48',
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={() => setEditingRegister(r)}>
            {t('common.edit')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              void onToggleRegisterStatus(r);
            }}
          >
            {r.status === 'active' ? t('common.deactivate') : t('common.activate')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">{t('settings_ops.title')}</h2>
          <p className="text-muted-foreground text-sm">{t('settings_ops.subtitle')}</p>
        </div>
        {can_manage_all ? (
          <div className="space-y-1">
            <label htmlFor="settings_branch" className="text-muted-foreground text-xs">
              {t('settings_ops.branch')}
            </label>
            <select
              id="settings_branch"
              value={branch_id}
              onChange={(e) => setBranchId(e.target.value)}
              className="border-input bg-background focus:ring-ring h-9 min-w-64 rounded-md border px-3 text-sm shadow-sm focus:outline-none focus:ring-2"
            >
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </header>

      <nav className="border-border flex flex-wrap gap-1 border-b">
        {visible_tabs.map((tab_key) => (
          <button
            key={tab_key}
            type="button"
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === tab_key
                ? 'border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground border-transparent'
            }`}
            onClick={() => setTab(tab_key)}
          >
            {t(`settings_ops.tabs.${tab_key}`)}
          </button>
        ))}
      </nav>

      {no_branch_selected ? (
        <p className="text-muted-foreground text-sm">{t('settings_ops.no_branch_selected')}</p>
      ) : null}

      {tab === 'workstations' ? (
        <CrudTable<Workstation & { kind: WorkstationKind }>
          rows={workstation_rows}
          columns={workstation_columns}
          loading={ws_query.loading}
          page={
            ws_query.data?.workstations.page ?? {
              total: 0,
              offset: ws_list.offset,
              limit: ws_list.limit,
              has_more: false,
            }
          }
          onPageChange={ws_list.setOffset}
          rowKey={(r) => r.id}
          empty_label_key="settings_ops.workstations.empty"
          toolbar={
            <CrudToolbar
              search={ws_list.search}
              onSearchChange={ws_list.setSearch}
              filters={
                <select
                  value={ws_list.status_filter}
                  onChange={(e) =>
                    ws_list.setStatusFilter(e.target.value as 'ACTIVE' | 'INACTIVE' | 'ALL')
                  }
                  className="border-input bg-background focus:ring-ring h-10 rounded-md border px-3 text-sm shadow-sm focus:outline-none focus:ring-2"
                >
                  <option value="ACTIVE">{t('status.active')}</option>
                  <option value="INACTIVE">{t('status.inactive')}</option>
                  <option value="ALL">{t('settings_ops.status_all')}</option>
                </select>
              }
              primary_action={
                <Button
                  type="button"
                  onClick={() => setCreatingWs(true)}
                  disabled={no_branch_selected}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  <span>{t('settings_ops.workstations.create')}</span>
                </Button>
              }
            />
          }
        />
      ) : null}

      {tab === 'consulting_rooms' ? (
        <CrudTable<ConsultingRoom>
          rows={room_rows}
          columns={room_columns}
          loading={rooms_query.loading}
          page={
            rooms_query.data?.consultingRooms.page ?? {
              total: 0,
              offset: rooms_list.offset,
              limit: rooms_list.limit,
              has_more: false,
            }
          }
          onPageChange={rooms_list.setOffset}
          rowKey={(r) => r.id}
          empty_label_key="settings_ops.consulting_rooms.empty"
          toolbar={
            <CrudToolbar
              search={rooms_list.search}
              onSearchChange={rooms_list.setSearch}
              filters={
                <select
                  value={rooms_list.status_filter}
                  onChange={(e) =>
                    rooms_list.setStatusFilter(e.target.value as 'ACTIVE' | 'INACTIVE' | 'ALL')
                  }
                  className="border-input bg-background focus:ring-ring h-10 rounded-md border px-3 text-sm shadow-sm focus:outline-none focus:ring-2"
                >
                  <option value="ACTIVE">{t('status.active')}</option>
                  <option value="INACTIVE">{t('status.inactive')}</option>
                  <option value="ALL">{t('settings_ops.status_all')}</option>
                </select>
              }
              primary_action={
                <Button
                  type="button"
                  onClick={() => setCreatingRoom(true)}
                  disabled={no_branch_selected}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  <span>{t('settings_ops.consulting_rooms.create')}</span>
                </Button>
              }
            />
          }
        />
      ) : null}

      {tab === 'cash_registers' ? (
        <CrudTable<CashRegister>
          rows={registers_query.data?.cashRegisters.items ?? []}
          columns={register_columns}
          loading={registers_query.loading}
          page={
            registers_query.data?.cashRegisters.page ?? {
              total: 0,
              offset: registers_list.offset,
              limit: registers_list.limit,
              has_more: false,
            }
          }
          onPageChange={registers_list.setOffset}
          rowKey={(r) => r.id}
          empty_label_key="settings_ops.cash_registers.empty"
          toolbar={
            <CrudToolbar
              search={registers_list.search}
              onSearchChange={registers_list.setSearch}
              filters={
                <select
                  value={registers_list.status_filter}
                  onChange={(e) =>
                    registers_list.setStatusFilter(e.target.value as 'ACTIVE' | 'INACTIVE' | 'ALL')
                  }
                  className="border-input bg-background focus:ring-ring h-10 rounded-md border px-3 text-sm shadow-sm focus:outline-none focus:ring-2"
                >
                  <option value="ACTIVE">{t('status.active')}</option>
                  <option value="INACTIVE">{t('status.inactive')}</option>
                  <option value="ALL">{t('settings_ops.status_all')}</option>
                </select>
              }
              primary_action={
                <Button
                  type="button"
                  onClick={() => setCreatingRegister(true)}
                  disabled={no_branch_selected}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  <span>{t('settings_ops.cash_registers.create')}</span>
                </Button>
              }
            />
          }
        />
      ) : null}

      {tab === 'species' ? (
        <section className="border-border bg-card/40 space-y-4 rounded-md border p-4">
          <header>
            <h3 className="text-base font-semibold">{t('settings_ops.species.title')}</h3>
            <p className="text-muted-foreground text-sm">{t('settings_ops.species.subtitle')}</p>
          </header>

          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-64 flex-1 space-y-1">
              <Label htmlFor="new_species">{t('settings_ops.species.new_label')}</Label>
              <Input
                id="new_species"
                value={new_species}
                onChange={(e) => setNewSpecies(e.target.value)}
                placeholder={t('settings_ops.species.new_placeholder')}
              />
            </div>
            <Button
              type="button"
              onClick={() => {
                void onAddSpecies();
              }}
              disabled={set_species_catalog_state.loading}
            >
              <Plus className="h-4 w-4" aria-hidden />
              <span>{t('settings_ops.species.add')}</span>
            </Button>
          </div>

          <ul className="space-y-2">
            {species_rows.length === 0 ? (
              <li className="text-muted-foreground text-sm">{t('settings_ops.species.empty')}</li>
            ) : (
              species_rows.map((item) => (
                <li
                  key={item.value}
                  className="border-border bg-background flex items-center justify-between rounded-md border px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{speciesLabel(item.value)}</span>
                    {item.is_system ? (
                      <Badge variant="muted">{t('settings_ops.species.fixed_badge')}</Badge>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={item.is_system || set_species_catalog_state.loading}
                    onClick={() => {
                      void onRemoveSpecies(item.value);
                    }}
                  >
                    {t('common.remove')}
                  </Button>
                </li>
              ))
            )}
          </ul>
        </section>
      ) : null}

      {tab === 'display_boards' ? (
        <section className="border-border bg-card/40 space-y-4 rounded-md border p-4">
          <header>
            <h3 className="text-base font-semibold">{t('settings_ops.display_boards.title')}</h3>
            <p className="text-muted-foreground text-sm">
              {t('settings_ops.display_boards.subtitle')}
            </p>
          </header>

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
            <div className="space-y-2">
              <Label htmlFor="waiting_room_playlist">
                {t('settings_ops.display_boards.playlist_label')}
              </Label>
              <Input
                id="waiting_room_playlist"
                value={waiting_room_playlist}
                onChange={(e) => setWaitingRoomPlaylist(e.target.value)}
                placeholder={t('settings_ops.display_boards.playlist_placeholder')}
                disabled={
                  waiting_room_display_settings_query.loading ||
                  set_waiting_room_display_settings_state.loading
                }
              />
              <p className="text-muted-foreground text-xs">
                {t('settings_ops.display_boards.playlist_hint')}
              </p>
            </div>

            <div className="border-border bg-background rounded-md border p-3">
              <p className="text-muted-foreground text-xs font-semibold uppercase">
                {t('settings_ops.display_boards.current_label')}
              </p>
              <p className="mt-2 break-all text-sm font-medium">
                {waiting_room_display_settings_query.data?.waitingRoomDisplaySettings
                  .youtube_playlist_id || t('settings_ops.display_boards.not_configured')}
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => {
                void onSaveWaitingRoomPlaylist();
              }}
              disabled={
                waiting_room_display_settings_query.loading ||
                set_waiting_room_display_settings_state.loading
              }
            >
              {t('common.save')}
            </Button>
          </div>
        </section>
      ) : null}

      {creating_ws ? (
        <WorkstationFormDialog
          title={t('settings_ops.workstations.create')}
          submitting={create_workstation_state.loading}
          onSubmit={onCreateWorkstation}
          onCancel={() => setCreatingWs(false)}
        />
      ) : null}
      {editing_ws ? (
        <WorkstationFormDialog
          title={t('settings_ops.workstations.edit')}
          initial_values={{
            display_name: editing_ws.display_name,
            kind: mapWorkstationKind(editing_ws.kind),
          }}
          submitting={update_workstation_state.loading}
          onSubmit={onUpdateWorkstation}
          onCancel={() => setEditingWs(null)}
        />
      ) : null}

      {creating_room ? (
        <ConsultingRoomFormDialog
          title={t('settings_ops.consulting_rooms.create')}
          workstation_options={room_workstation_options}
          submitting={create_room_state.loading}
          onSubmit={onCreateRoom}
          onCancel={() => setCreatingRoom(false)}
        />
      ) : null}
      {editing_room ? (
        <ConsultingRoomFormDialog
          title={t('settings_ops.consulting_rooms.edit')}
          initial_values={{
            name: editing_room.name,
            display_order: editing_room.display_order,
            zone_type: editing_room.zone_type,
            workstation_id: editing_room.workstation_id ?? '',
          }}
          workstation_options={room_workstation_options}
          submitting={update_room_state.loading}
          onSubmit={onUpdateRoom}
          onCancel={() => setEditingRoom(null)}
        />
      ) : null}

      {creating_register ? (
        <CashRegisterFormDialog
          title={t('settings_ops.cash_registers.create')}
          workstation_options={register_workstation_options}
          submitting={create_register_state.loading}
          onSubmit={onCreateRegister}
          onCancel={() => setCreatingRegister(false)}
        />
      ) : null}
      {editing_register ? (
        <CashRegisterFormDialog
          title={t('settings_ops.cash_registers.edit')}
          initial_values={{
            name: editing_register.name,
            workstation_id: editing_register.workstation_id ?? '',
          }}
          workstation_options={register_workstation_options}
          submitting={update_register_state.loading}
          onSubmit={onUpdateRegister}
          onCancel={() => setEditingRegister(null)}
        />
      ) : null}
    </div>
  );
}
